// web/src/billing/PricingPage.tsx
//
// Public route at /pricing. Same URL serves prospects, signed-in free
// users, and paying users — the per-card CTA is the variation point.
// Header + footer come from MarketingLayout.
import { useState } from "react";
import { CadenceToggle, type Cadence } from "./CadenceToggle";
import { PlanCard, type ViewerState } from "./PlanCard";
import {
  useSubscription, useCheckoutSession, usePortalSession,
} from "./api";
import { Seo } from "@/marketing/Seo";


const FREE_BULLETS = [
  "Trading dashboard with two panes",
  "News headlines + clusters",
  "Basic alerts",
];
const PRO_BULLETS = [
  "Tapelens AI Copilot",
  "Full historical data",
  "Advanced alerts and filters",
];
const ANALYST_BULLETS = [
  "AI Copilot powered by our smarter, more capable model",
  "Premium analyses",
  "Premium briefings",
];


function priceLabel(tier: "pro" | "analyst", cadence: Cadence): string {
  if (cadence === "monthly") return tier === "pro" ? "$19.99" : "$49.99";
  return tier === "pro" ? "$199.99" : "$499.99";
}


export function PricingPage() {
  const [cadence, setCadence] = useState<Cadence>("monthly");
  const sub = useSubscription();
  const checkout = useCheckoutSession();
  const portal = usePortalSession();

  let viewerState: ViewerState;
  if (sub.data === undefined) {
    viewerState = { kind: "signed_out" };
  } else {
    viewerState = {
      kind: "signed_in",
      currentTier: sub.data.tier,
      currentCadence: sub.data.billing_cadence,
    };
  }

  async function startTrial(args: { plan: "pro" | "analyst"; cadence: Cadence }) {
    const res = await checkout.mutateAsync(args);
    window.location.href = res.url;
  }

  async function switchPlan() {
    const res = await portal.mutateAsync({});
    window.location.href = res.url;
  }

  return (
    <div className="container max-w-5xl py-12 mx-auto px-4">
      <Seo
        title="Pricing — Tapelens"
        description="Start free. Upgrade when you need the AI Copilot. Monthly or annual billing (annual saves ~20%)."
        path="/pricing"
      />
      <div className="text-center mb-8 space-y-3">
        <h1 className="text-3xl font-semibold">Pricing</h1>
        <p className="text-muted-foreground">
          Start free. Upgrade when you need the AI Copilot.
        </p>
        <div className="flex justify-center pt-3">
          <CadenceToggle value={cadence} onChange={setCadence} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <PlanCard
          tier="free" cadence={cadence} price="$0"
          bullets={FREE_BULLETS} viewerState={viewerState}
          onStartTrial={startTrial} onSwitchPlan={switchPlan}
        />
        <PlanCard
          tier="pro" cadence={cadence} price={priceLabel("pro", cadence)}
          bullets={PRO_BULLETS} viewerState={viewerState}
          onStartTrial={startTrial} onSwitchPlan={switchPlan}
        />
        <PlanCard
          tier="analyst" cadence={cadence} price={priceLabel("analyst", cadence)}
          bullets={ANALYST_BULLETS} viewerState={viewerState}
          onStartTrial={startTrial} onSwitchPlan={switchPlan}
        />
      </div>
    </div>
  );
}
